import { withRouter } from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep';
import ActionText from '../ActionText';
import { APPLICATION_PATHS } from '../../application-core/constants';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import PropTypes from 'prop-types';
import { UserConsumer } from '../../core/providers/UserProvider';
import { ENTITLEMENTS as APPENTITLEMENTS } from '../../core/utils/constants';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { getGraphFromProject, makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import { withStyles } from '@material-ui/core/styles';
import React, { Component } from 'react';
import { globalStyles as styles } from '../style/gstheme';
import SnapshotConfigurationStructure from './SnapshotConfigurationStructure';
import SnapshotConfigurationDetail from './SnapshotConfigurationDetail';
import SelectProjectDialog from '../../core/project/SelectProjectDialog';

class SnapshotManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      configurations: [],
      filteredConfigurations: [],
      selectedConfiguration: null,
      isLoading: true,
      text: '',
      isErrored: null,
      updatedAt: Date.now()
    };
    this.cancellable = null;
    this.cancellable1 = null;
  }

  doGetUsers() {
    this.cancellable = makeCancelable(GS.subscriptionUsers());
    return this.cancellable;
  }

  componentDidMount() {
    this.getData();
  }

  getData = async () => {
    try {
      const users = await this.doGetUsers().promise;
      const configurations = await this.doGetConfigurations().promise;
      const filteredConfigurations = configurations.map(configuration => configuration.id);
      this.setState({ users, configurations, filteredConfigurations, isLoading: false });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  doGetConfigurations() {
    this.cancellable = makeCancelable(GS.snapshotConfigurations());
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.check) clearTimeout(this.check);
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable1) this.cancellable1.cancel();
  }

  doCreateConfiguration(project) {
    this.cancellable1 = makeCancelable(GS.createSnapshotConfiguration(project));
    return this.cancellable1;
  }

  checkConfiguration(configurationIRI, projectIRI, exists = true) {
    this.check = setTimeout(async () => {
      clearTimeout(this.check);
      try {
        GS.handle("check-configuration");
        const configurations = await this.doGetConfigurations().promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (configurations.some(configuration => configuration.id === configurationIRI) === exists) {
          const filteredConfigurations = configurations.map(configuration => configuration.id);
          this.setState({
            checking: false,
            configurations,
            filteredConfigurations,
            ...(exists) && {
              selectedConfiguration: configurationIRI,
              selectedProject: projectIRI
            }
          }, () => {
            this.onUpdate(this.state.text);
          });
        }
        else {
          this.checkConfiguration(configurationIRI, projectIRI, exists);
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ checking: false, isErrored: e });
      }
    }, 1000);
  }

  createConfiguration = async (projectIRI) => {
    try {
      GS.handle("create-configuration");
      // Make sure project loaded
      await GS.loadData([projectIRI], {}, getGraphFromProject(projectIRI));
      const response = await this.doCreateConfiguration(projectIRI).promise;
      const responseData = await response.json();
      const configurationIRI = responseData.snapshotConfiguration;
      if (configurationIRI) {
        this.setState({ checking: true }, () => this.checkConfiguration(configurationIRI, projectIRI));
      }
      else {
        this.setState({
          isErrored: {
            message: "Cannot create configuration."
          }
        });
      }
    }
    catch (e) {
      if (!this.cancellable1 || (this.cancellable1 && !this.cancellable1.isCancelled())) this.setState({ isErrored: e });
    }
  };

  selectConfiguration = (id) => {
    const selectedConfigurationData = this.state.configurations.find(configuration => configuration.id === id);
    const selectedProject = selectedConfigurationData.project;
    this.setState({ selectedConfiguration: id, selectedProject });
  };

  onUpdate = (text) => {
    let filteredConfigurations = [];
    const currentConfigurations = this.state.configurations;
    for (let configuration of currentConfigurations) {
      const projectData = GS.data(configuration.project, getGraphFromProject(configuration.project), false);
      if (projectData && projectData.title[projectData.defaultLang].toLowerCase().includes(text.toLowerCase())) {
        filteredConfigurations.push(configuration.id);
      }
      if (configuration.title && configuration.title[Object.keys(configuration.title)[0]].toLowerCase().includes(text.toLowerCase())) {
        filteredConfigurations.push(configuration.id);
      }
    }
    this.setState({ filteredConfigurations, text });
  };

  doDeleteConfiguration(id) {
    this.cancellable = makeCancelable(GS.deleteSnapshotConfiguration(id));
    return this.cancellable;
  }

  deleteConfiguration = (configurationIRI) => {
    try {
      this.setState({ selectedConfiguration: null, selectedProject: null, checking: true }, async () => {
        await this.doDeleteConfiguration(configurationIRI).promise;
        this.checkConfiguration(configurationIRI, null, false);
      });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  updateConfiguration = (configurationIRI, snapshotPeriod, snapshotCount) => {
    GS.updateSnapshotConfiguration(configurationIRI, {
      snapshotPeriod,
      snapshotCount
    });
    let newConfigurations = cloneDeep(this.state.configurations);
    const updatedConfiguration = newConfigurations.find(configuration => configuration.id === configurationIRI);
    updatedConfiguration.snapshotCount = snapshotCount;
    updatedConfiguration.snapshotPeriod = snapshotPeriod;
    this.setState({ configurations: newConfigurations });
  };

  doTakeSnapshot(id, payload) {
    this.cancellable = makeCancelable(GS.takeSnapshot(id, payload));
    return this.cancellable;
  }

  checkSnapshot(snapshotIRI) {
    this.check = setTimeout(async () => {
      clearTimeout(this.check);
      try {
        GS.handle("check-snapshot");
        const result = await GS.find({
          value: snapshotIRI,
          graph: GS.getActiveSubscription()
        });
        if (result && result.exists) {
          this.setState({
            updatedAt: Date.now(),
            checkingSnapshot: false,
          });
        }
        else {
          if (this.cancellable && this.cancellable.isCancelled()) return;
          this.checkSnapshot(snapshotIRI);
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ checking: false, isErrored: e });
      }
    }, 10000);
  }

  takeSnapshot = async (configurationIRI, payload) => {
    try {
      GS.handle("take-snapshot");
      const response = await this.doTakeSnapshot(configurationIRI, payload).promise;
      const responseData = await response.json();
      const snapshotIRI = responseData.snapshot;
      if (snapshotIRI) {
        this.setState({ checkingSnapshot: true }, () => this.checkSnapshot(snapshotIRI));
      }
      else {
        this.setState({
          isErrored: {
            message: "Cannot take snapshot."
          }
        });
      }
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  loadEntitlements(user, projectIRI) {
    this.cancellable = makeCancelable(GS.getProjectEntitlements(user, projectIRI));
    return this.cancellable;
  }

  checkProjectReady = (user, projectIRI, projectType) => {
    this.check = setTimeout(async () => {
      clearTimeout(this.check);
      try {
        const entitlements = await this.loadEntitlements(user, projectIRI).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (entitlements && 'hasEntitlement' in entitlements) {
          this.setState({ checkingComplete: false });
          const projectLink = projectType === "https://grafsync.graphifi.com/schema/gs/TaxonomyProject" ?
            APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/detail#project=' + projectIRI
            :
            projectType === "https://grafsync.graphifi.com/schema/gs/DataProject" ?
              APPLICATION_PATHS.DATA_MANAGER + '/project/detail#project=' + projectIRI
              :
              APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/detail#project=' + projectIRI;
          this.props.history.push(projectLink);
        }
        else {
          this.checkProjectReady(user, projectIRI, projectType);
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ checkingComplete: false, isErrored: e });
      }
    }, 2500);
  };

  doRestoreSnapshot(snapshotGraph, resource) {
    this.cancellable = makeCancelable(GS.operate("restore", snapshotGraph, {
      resource
    }));
    return this.cancellable;
  }

  restoreSnapshot = user => async (projectIRI, projectType, snapshotGraph, snapshotIRI) => {
    try {
      GS.handle("restore-snapshot");
      await this.doRestoreSnapshot(snapshotGraph, snapshotIRI).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ checkingComplete: true }, () => this.checkProjectReady(user, projectIRI, projectType));
    }
    catch (e) {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ checkingComplete: false, isErrored: e });
    }
  };

  render() {

    const { classes } = this.props;
    const { isLoading, configurations, checking, checkingSnapshot, updatedAt, checkingComplete, users,
      filteredConfigurations, selectedConfiguration, selectedProject, isErrored } = this.state;
    const displayConfigurations = configurations.filter(configuration => filteredConfigurations.includes(configuration.id));
    const selectedConfigurationData = configurations.find(configuration => configuration.id === selectedConfiguration);
    const excludedProjects = configurations.map(configuration => configuration.project);

    if (isErrored) return <Errored dataTest="sm-error" data={isErrored} />;
    if (isLoading) return <Loading />;

    return (
      <UserConsumer>
        {({ user, entitlements: appEntitlements, subscriptions, invitations, activeSubscription }) => {
          const accessedSubscriptions = [...subscriptions, ...invitations];
          const subscription = accessedSubscriptions && accessedSubscriptions.find(resource => {
            if (resource.type === "Subscription" && resource.id === activeSubscription) return true;
            if (resource.type === "Invitation" && resource.subscription === activeSubscription) return true;
            return false;
          });
          return <div>
            <SnapshotConfigurationStructure
              onUpdate={this.onUpdate}
              maxConfigurations={subscription.maxSnapshotConfigurations}
              configurations={configurations}
              displayConfigurations={displayConfigurations}
              selectedConfiguration={selectedConfiguration}
              onSelectConfiguration={this.selectConfiguration} />
            <div className={classes.dashboardPanel}>
              <div className={classes.dashboardAdd}>
                {configurations.length < subscription.maxSnapshotConfigurations &&
                  appEntitlements.includes(APPENTITLEMENTS.MANAGE_SNAPSHOTS) &&
                  <SelectProjectDialog exclude={excludedProjects} onAddProject={this.createConfiguration} />
                }
              </div>
              {selectedConfiguration ?
                <SnapshotConfigurationDetail
                  key={selectedConfiguration + updatedAt}
                  handleNotAvailable={true}
                  configuration={selectedConfigurationData}
                  graph={getGraphFromProject(selectedProject)}
                  id={selectedProject}
                  users={users}
                  onTakeSnapshot={this.takeSnapshot}
                  onRestoreData={this.restoreSnapshot(user)}
                  onUpdateConfiguration={this.updateConfiguration}
                  onDeleteConfiguration={this.deleteConfiguration} />
                :
                (configurations.length > 0 ?
                  <ActionText offset="180px">Select a project to view and edit its detail.</ActionText>
                  :
                  <div style={{ textAlign: 'center', marginTop: '80px' }}>
                    <img style={{ height: '100px' }} src="/images/snapshot-black.svg" alt="" />
                    <ActionText dataTest="add-snapshot-info" offset="30px">Add snapshots to make backups and allow versioning.</ActionText>
                  </div>
                )
              }
            </div>
            {(checking || checkingSnapshot || checkingComplete) &&
              <div>
                <Dialog data-test="snapshot-wait" open={true} aria-labelledby="waiting-dialog-title" >
                  <DialogTitle
                    disableTypography
                    className={classes.mainHeading}
                    id="waiting-dialog-title">
                    {
                      checking ?
                        "Processing Snapshot Configuration"
                        :
                        checkingComplete ?
                          "Waiting for Restore"
                          :
                          "Taking Snapshot"
                    }
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText className={classes.dialogText}>
                      {
                        checking ?
                          "Please wait. Your snapshot configuration will be processed shortly."
                          :
                          checkingComplete ?
                            "Please wait: The restore is underway."
                            :
                            "Please wait. The snapshot is being taken."
                      }
                    </DialogContentText>
                  </DialogContent>
                </Dialog>
              </div>
            }
          </div>;
        }}
      </UserConsumer>
    );
  }
}

SnapshotManager.propTypes = {
  classes: PropTypes.object,
  history: PropTypes.object
};

export default withRouter(withStyles(styles)(SnapshotManager));